import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import socket from "../socket";
import { motion, AnimatePresence } from "framer-motion";

// 🧩 Service images
import plumberImg from "../assets/services/plumber.png";
import electricianImg from "../assets/services/electrician.png";
import cleaningImg from "../assets/services/cleaning.png";
import lawnCleaningImg from "../assets/services/lawn-cleaning.png";
import petSittingImg from "../assets/services/pet-sitting.png";
import movingImg from "../assets/services/moving.png";
import securityImg from "../assets/services/security.png";
import haircutImg from "../assets/services/haircut.png";
import makeupImg from "../assets/services/makeup.png";
import furnitureAssemblyImg from "../assets/services/furniture-assembly.png";
import roofCleaningImg from "../assets/services/roof-cleaning.png";
import childCareImg from "../assets/services/child-care.png";
import handymanImg from "../assets/services/handyman.png";
import junkRemovalImg from "../assets/services/junk-removal.png";
import snowRemovalImg from "../assets/services/snow-removal.png";
import windowCleaningImg from "../assets/services/window-cleaning.png";
import cookingImg from "../assets/services/cooking.png";
import pedicureManicureImg from "../assets/services/pedicure-manicure.png";
import facialSkincareImg from "../assets/services/facial-skincare.png";
import threadingWaxingImg from "../assets/services/threading-waxing.png";
import spaMassageImg from "../assets/services/spa-massage.png";
import painterImg from "../assets/services/painter.png";
import eventPhotographerImg from "../assets/services/event-photographer.png";
import personalChefImg from "../assets/services/personal-chef.png";
import carpetCleaningImg from "../assets/services/carpet-cleaning.png";
import pressureWashingImg from "../assets/services/pressure-washing.png";
import tvMountingImg from "../assets/services/tv-mounting.png";
import applianceRepairImg from "../assets/services/appliance-repair.png";
import flooringRepairImg from "../assets/services/flooring-repair.png";
import poolCleaningImg from "../assets/services/pool-cleaning.png";
import bridalMakeupImg from "../assets/services/bridal-makeup.png";
import tutorImg from "../assets/services/tutor.png";

const API_BASE = process.env.REACT_APP_API_BASE;

const serviceImages = {
  Plumber: plumberImg,
  Electrician: electricianImg,
  Cleaning: cleaningImg,
  Painter: painterImg,
  "Event Photographer": eventPhotographerImg,
  "Personal Chef": personalChefImg,
  "Carpet Cleaning": carpetCleaningImg,
  "Pressure Washing": pressureWashingImg,
  "TV Mounting": tvMountingImg,
  "Appliance Repair": applianceRepairImg,
  "Flooring Repair": flooringRepairImg,
  "Pool / Hot Tub Cleaning": poolCleaningImg,
  "Bridal Makeup": bridalMakeupImg,
  "Tutor / Teaching": tutorImg,

  "Lawn Cleaning": lawnCleaningImg,
  "Pet Sitting / Walking": petSittingImg,
  Moving: movingImg,
  Security: securityImg,
  Barber: haircutImg,
  "Haircut & Styling (Women)": haircutImg,
  "Makeup Artist (Event)": makeupImg,
  "Furniture Assembly": furnitureAssemblyImg,
  "Roof Cleaning": roofCleaningImg,
  "Child Care": childCareImg,
  Handyman: handymanImg,
  "Junk Removal": junkRemovalImg,
  "Snow Removal": snowRemovalImg,
  "Window Cleaning": windowCleaningImg,
  "Cooking / Meal Prep": cookingImg,
  "Pedicure & Manicure": pedicureManicureImg,
  "Facial & Skin Care": facialSkincareImg,
  "Threading / Waxing": threadingWaxingImg,

  "At-Home Spa (Massage)": spaMassageImg,
};

const YourJobs = () => {
  const navigate = useNavigate();
  const userEmail = localStorage.getItem("userEmail");
  const customerId = localStorage.getItem("customerId");
  const [inProgressJobs, setInProgressJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [cancelledJobs, setCancelledJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("inprogress");

  const [modal, setModal] = useState(null); // {type, job, title, message}
  const [jobMenu, setJobMenu] = useState(null);
  // keep one debounce timer that we fully clean on unmount
  const fetchTimerRef = useRef(null);

// ✅ NEW: Safety cleanup
  useEffect(() => {
    return () => {
      // Stop all animations and modals
      setModal(null);
      clearTimeout(fetchTimerRef.current);
      socket.removeAllListeners(); // Kill socket listeners
      document.body.style.overflow = "auto";
    };
  }, []);

  // ===== Helpers
  const getServiceImage = (type) => serviceImages[type] || plumberImg;
  const idOf = (maybeObjOrId) =>
    typeof maybeObjOrId === "object" && maybeObjOrId?._id
      ? maybeObjOrId._id
      : String(maybeObjOrId || "");

  const safeFetch = (fn) => {
    clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(fn, 400);
  };

  // ===== Fetch Jobs
  const fetchJobs = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/customers/jobs/user-jobs/${userEmail}`
      );

      const normalize = (jobs) =>
        (jobs || []).map((j) => ({
          ...j,
          status: (j.status || "").trim(),
          isReopened: j.isReopened || (j.repostCount && j.repostCount > 0),
        }));

      const all = normalize([
        ...(res.data.pending || []),
        ...(res.data.assigned || []),
        ...(res.data.completed || []),
        ...(res.data.cancelled || []),
      ]);

      const inprogress = all.filter((j) =>
        [
          "pending",
          "reopened",
          "assigned",
          "worker_completed",
          "Worker_completed",
          "workerCompleted",
          "dispute",
        ].includes(j.status)
      );

      const completed = all.filter((j) =>
        ["completed", "customer_confirmed", "auto_confirmed"].includes(j.status)
      );

      const cancelled = all.filter((j) =>
        ["cancelled", "refunded", "partial_refund"].includes(j.status)
      );

      setInProgressJobs(inprogress);
      setCompletedJobs(completed);
      setCancelledJobs(cancelled);
    } catch (err) {
      console.error("❌ Failed to fetch jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (userEmail) fetchJobs();
    return () => clearTimeout(fetchTimerRef.current);
  }, [userEmail]);

  useEffect(() => {
  const handleClick = (e) => {
    // Do NOT close menu if clicking inside
    if (!e.target.closest(".three-dot-menu")) {
      setJobMenu(null);
    }
  };
  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, []);

  // ===== Sockets — keep logic, just ensure full cleanup
  useEffect(() => {
    if (!customerId) return;

    socket.emit("registerCustomer", customerId);

    const handleBidReceived = () => {
      toast.success("📩 New bid received on one of your jobs!");
      safeFetch(fetchJobs);
    };

    const handleJobUpdate = (update) => {
      switch (update?.status) {
        case "worker_completed":
          toast("🛠️ Worker marked a job complete. Please confirm or dispute.", {
            icon: "⚙️",
          });
          break;
        case "completed":
        case "customer_confirmed":
        case "auto_confirmed":
          toast.success("✅ Job marked complete!");
          break;
        case "cancelled_by_worker":
          toast.error("❌ Worker cancelled your job. It’s reopened for new bids.");
          break;
        case "cancelled_by_customer":
          toast("🚫 You cancelled this job. Refund in progress.", { icon: "💸" });
          break;
        case "reopened":
          toast("🔁 Job reopened. New workers will be notified.", { icon: "📢" });
          break;
        default:
          toast(`⚡ Job updated: ${update?.status || "changed"}`);
      }
      safeFetch(fetchJobs);
    };

    const handleBidChange = (info) => {
      if (info?.type === "request")
        toast(`💬 ${info.workerName || "A worker"} requested a bid change.`, {
          icon: "💰",
        });
      else if (info?.type === "accepted")
        toast.success(`✅ You accepted a bid change for ${info.jobTitle || "a job"}`);
      else if (info?.type === "rejected")
        toast.error(`❌ You rejected a bid change for ${info.jobTitle || "a job"}`);
      safeFetch(fetchJobs);
    };

    const handleRefresh = () => {
      safeFetch(fetchJobs);
    };

    socket.on("job:bidReceived", handleBidReceived);
    socket.on("job:update", handleJobUpdate);
    socket.on("bid:changeUpdate", handleBidChange);
    socket.on("job:refresh", handleRefresh);

    socket.on("connect", () => {
      socket.emit("registerCustomer", customerId);
    });

    return () => {
      socket.off("job:bidReceived", handleBidReceived);
      socket.off("job:update", handleJobUpdate);
      socket.off("bid:changeUpdate", handleBidChange);
      socket.off("job:refresh", handleRefresh);
      socket.off("connect");
      clearTimeout(fetchTimerRef.current);
    };
  }, [customerId]);

  // ===== Actions
  const handleAcceptBidChange = async (bidId) => {
    try {
      await axios.post(`${API_BASE}/api/customers/jobs/bids/change/accept`, {
        bidId,
      });
      toast.success("Bid change accepted ✅");
      fetchJobs();
    } catch (err) {
      console.error("❌ Accept error:", err);
      toast.error("Failed to accept bid change");
    }
  };

  const handleRejectBidChange = async (bidId) => {
    try {
      await axios.post(`${API_BASE}/api/customers/jobs/bids/change/reject`, {
        bidId,
      });
      toast.success("Bid change rejected ❌");
      fetchJobs();
    } catch (err) {
      console.error("❌ Reject error:", err);
      toast.error("Failed to reject bid change");
    }
  };

  const handleConfirmJob = async (job) => {
    try {
      await axios.post(`${API_BASE}/api/payment/jobs/${job._id}/confirm`, {
        customerId: job.customerId,
      });
      toast.success("✅ Job confirmed successfully");
      fetchJobs();
    } catch (err) {
      console.error("❌ Confirm error:", err);
      toast.error("Failed to confirm job");
    }
  };

  const handleDisputeJob = async (job, reasonText) => {
    const r = reasonText || prompt("Please provide a reason for dispute:");
    if (!r) return;
    try {
      await axios.post(`${API_BASE}/api/payment/jobs/${job._id}/dispute`, {
        customerId: job.customerId,
        reason: r,
      });
      toast.success("⚠️ Dispute filed");
      fetchJobs();
    } catch (err) {
      console.error("❌ Dispute error:", err);
      toast.error("Failed to file dispute");
    }
  };

  const handleCancelJob = (job) => {
    const msg =
      job.status === "pending" || job.status === "reopened"
        ? "Are you sure you want to cancel this job? A refund will be processed within 1–5 business days."
        : "This job is assigned. Cancelling now will deduct a $4.99 cancellation fee. Continue?";

    setModal({
      type: "cancel",
      job,
      title: "Confirm Job Cancellation",
      message: msg,
    });
  };

  // ===== Job Card
  const JobCard = ({ job, statusColor, isCancelled }) => {
    const service = job.serviceType || job.jobTitle || "Service";
    const jobDate = job.scheduledAt ? new Date(job.scheduledAt) : null;
    const now = new Date();
    const isToday = jobDate && jobDate.toDateString() === now.toDateString();
    const isPast = jobDate && now > jobDate;

    const topDate = jobDate
      ? jobDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : "N/A";
    const topTime = jobDate
      ? jobDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "";

    const price = job.assignedPrice || job.budget || 0;
    const priceLabel = job.assignedPrice ? "Price" : "Budget";

    // Determine if there is a visible bid-change request for this job
    const visibleBidChanges =
      job.bids?.filter((bid) => {
        const cr = bid?.changeRequest;
        const hasChange =
          cr && ["pending", "approved", "rejected"].includes(cr.status || "");
        if (!hasChange) return false;

        if (job.assignedTo) {
          return (
            idOf(bid.workerId) === idOf(job.assignedTo) // only show assigned worker's change
          );
        }
        // when not assigned yet, only pending changes make sense
        return cr.status === "pending";
      }) || [];

    return (
       <div
  className="bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-xl p-5 md:p-6 mb-8 transition-all"
>

       {/* top row */}
<div className="flex items-start justify-between mb-4">

  {/* LEFT: Date + Time */}
  <div>
    <p className="text-sm font-semibold text-gray-800">{topDate}</p>
    <p className="text-xs text-gray-400">{topTime}</p>
  </div>

    {/* RIGHT: PRICE + MENU */}
<div className="flex flex-col items-end gap-1">

  {/* BIG BLUE PRICE */}
  <p className="text-xl font-bold text-blue-600">
    ${price}
  </p>

{/* THREE DOTS MENU — only show when ASSIGNED */}
{job.status === "assigned" && (
  <div
    className="relative three-dot-menu"
    onClick={(e) => e.stopPropagation()}   // prevent card click / refresh
  >
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setJobMenu(jobMenu === job._id ? null : job._id);
      }}
      className="p-2 rounded-full hover:bg-gray-200"
    >
      ⋮
    </button>

    {jobMenu === job._id && (
      <div
        className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-xl z-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* VIEW WORKER PROFILE */}
        <button
          onClick={() => {
            navigate(`/worker-profile/${idOf(job.assignedTo)}`);
            setJobMenu(null);
          }}
          className="block w-full text-left px-4 py-2 hover:bg-gray-100"
        >
          View Worker Profile
        </button>

        {/* FILE DISPUTE */}
        <button
          onClick={() => {
            setJobMenu(null);
            setModal({
              type: "dispute",
              job,
              title: "File Job Dispute",
              message: "Please describe the issue with this job before filing a dispute.",
            });
          }}
          className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-yellow-600"
        >
          File Dispute
        </button>

        {/* SUPPORT */}
        <button
          onClick={() => {
            navigate("/help");
            setJobMenu(null);
          }}
          className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-blue-600"
        >
          Support
        </button>
      </div>
    )}
  </div>
)}

</div>

</div>


        {/* middle section */}
        <div className="flex items-center gap-4 mb-3">
          <img
            src={getServiceImage(service)}
            alt={service}
            className="w-20 h-20 rounded-xl object-cover border border-gray-100 shadow-sm"
          />
          <div className="flex-1 min-w-0">
            <h3 className={`text-lg font-semibold ${statusColor} truncate`}>
              {service}
            </h3>
            {job.description && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {job.description}
              </p>
            )}
            {/* 🔹 Assigned Worker Contact Section */}
{job.status === "assigned" && job.workerName && (
  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
    <p className="text-sm font-semibold text-blue-700 mb-1">Assigned Worker</p>

    <div className="flex items-center gap-3">

      {/* Initial avatar only */}
      <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
        {job.workerName.charAt(0).toUpperCase()}
      </div>

      <div>
        <p className="text-sm text-gray-900 font-medium">
          👤 {job.workerName}
        </p>

        {job.workerPhone && (
          <p className="text-sm text-gray-700 mt-0.5">
            📞 {job.workerPhone}
          </p>
        )}
      </div>

    </div>
  </div>
)}

          </div>
        </div>

        {/* bid-change panel(s) */}
        {visibleBidChanges.map((bid) => (
          <div
            key={bid._id}
            className={`rounded-lg p-3 mt-3 border ${
              bid.changeRequest.status === "pending"
                ? "bg-yellow-50 border-yellow-300"
                : bid.changeRequest.status === "approved"
                ? "bg-green-50 border-green-300"
                : "bg-red-50 border-red-300"
            }`}
          >
            <p className="text-sm font-medium">
              💬 {job.assignedTo ? "Assigned worker" : "A worker"} requested:
            </p>
            <p className="text-sm">
              New Price: <strong>${bid.changeRequest.newPrice}</strong>
            </p>
            {bid.changeRequest.message && (
              <p className="text-xs italic text-gray-700 mt-1">
                “{bid.changeRequest.message}”
              </p>
            )}

            {bid.changeRequest.status === "pending" && (
              <div className="flex gap-3 mt-2">
                <button
                  onClick={() => handleAcceptBidChange(bid._id)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
                >
                  Accept
                </button>
                <button
                  onClick={() => handleRejectBidChange(bid._id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
                >
                  Reject
                </button>
              </div>
            )}

            {bid.changeRequest.status === "approved" && (
              <p className="text-green-700 mt-2 text-sm">
                ✅ You accepted this change. New price: ${bid.changeRequest.newPrice}
              </p>
            )}
            {bid.changeRequest.status === "rejected" && (
              <p className="text-red-700 mt-2 text-sm">❌ You rejected this change.</p>
            )}
          </div>
        ))}

        {/* status notes */}
        {["Worker_completed", "worker_completed", "workerCompleted"].includes(
          job.status
        ) && (
          <p className="text-yellow-600 text-sm mt-3">
            ⏳ Worker marked this job complete. Waiting for your action.
          </p>
        )}
        {job.status === "dispute" && (
          <p className="text-red-600 text-sm mt-3">
            ⚠️ Job is in dispute. Admin will review.
          </p>
        )}
        {job.status === "customer_confirmed" && (
          <p className="text-green-600 text-sm mt-3">
            ✅ You confirmed this job as complete.
          </p>
        )}
        {job.status === "auto_confirmed" && (
          <p className="text-green-600 text-sm mt-3">
            ✅ Auto-confirmed after 48 hours.
          </p>
        )}
        {isCancelled && (
          <p className="text-red-600 text-sm mt-3">
            💸{" "}
            {job.paymentStatus === "refunded"
              ? "Full refund processed."
              : `Refunded: $${(job.refundAmount || 0).toFixed(
                  2
                )} (after $4.99 fee)`}
          </p>
        )}

        {/* bottom buttons — EXACT logic preserved */}
        <div className="flex flex-wrap gap-3 mt-4">
          {/* Chat only when assigned (or pending with assignedTo visible in your older flow) */}
          {job.status === "assigned" && (
  <button
    onClick={() => {
      navigate(
        `/chat/${idOf(job.customerId)}/${idOf(job.assignedTo)}/${job._id}`,
        {
          state: {
            workerName: job.workerName,
            workerPhone: job.workerPhone,
            workerEmail: job.workerEmail,
            workerPhoto: job.workerProfilePhoto,
          },
        }
      );
    }}
    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold"
  >
    Chat
  </button>
)}

          {/* Cancel only for pending/assigned/reopened */}
          {["pending", "assigned", "reopened"].includes(job.status) && (
            <button
              onClick={() => handleCancelJob(job)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold"
            >
              Cancel
            </button>
          )}

          {/* View details for pending/reopened (NOT for completed/cancelled) */}
          {["pending", "reopened"].includes(job.status) && (
            <button
              onClick={() => navigate(`/job/${job._id}`)}
              className="flex-1 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg font-semibold"
            >
              View Details
            </button>
          )}


          {/* Confirm/Dispute only when worker_completed and date is due */}
          {(isToday || isPast) &&
            ["Worker_completed", "worker_completed", "workerCompleted"].includes(
              job.status
            ) && (
              <>
                <button
                  onClick={() =>
                    setModal({
                      type: "confirm",
                      job,
                      title: "Confirm Job Completion",
                      message:
                        "Are you sure you want to confirm this job and release payment to the worker?",
                    })
                  }
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold"
                >
                  Confirm Completion
                </button>
                <button
                  onClick={() =>
                    setModal({
                      type: "dispute",
                      job,
                      title: "File Job Dispute",
                      message:
                        "Please describe the issue with this job before filing a dispute.",
                    })
                  }
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-semibold"
                >
                  File Dispute
                </button>
              </>
            )}
        </div>
      </div>
    );
  };

  // ===== Centered Modal
// ==================== FIXED GLOBAL MODAL COMPONENT ====================
const ModalUI = ({ modal, setModal, handleConfirmJob, handleDisputeJob, fetchJobs }) => {
  const [localReason, setLocalReason] = React.useState("");

  if (!modal) return null;

  const { type, job, title, message } = modal;

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80] flex items-center justify-center px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setModal(null)}
      >
        <motion.div
          key="modal"
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center relative"
          initial={{ y: 50, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 50, opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-2xl font-extrabold text-gray-900 mb-3">{title}</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">{message}</p>

          {type === "dispute" && (
            <textarea
              placeholder="Enter reason for dispute..."
              value={localReason}
              onChange={(e) => setLocalReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 mb-5 resize-none focus:ring-2 focus:ring-blue-500 outline-none"
              rows={3}
            />
          )}

          <div className="flex justify-center gap-4">
            <button
              onClick={async () => {
                try {
                  if (type === "confirm") {
                    await handleConfirmJob(job);
                  } else if (type === "dispute") {
                    if (!localReason.trim()) return toast.error("Please provide a reason.");
                    await handleDisputeJob(job, localReason.trim());
                  } else if (type === "cancel") {
                    await axios.post(
                      `${API_BASE}/api/customers/jobs/cancel-job/${job._id}`
                    );
                    toast.success("Job cancelled successfully ❌");
                    fetchJobs();
                  }

                  setModal(null);
                  setLocalReason("");

                } catch (err) {
                  console.error(err);
                }
              }}
              className={`px-6 py-2 rounded-full font-semibold text-white ${
                type === "confirm"
                  ? "bg-green-600 hover:bg-green-700"
                  : type === "cancel"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-yellow-500 hover:bg-yellow-600"
              }`}
            >
              Yes
            </button>

            <button
              onClick={() => setModal(null)}
              className="px-6 py-2 rounded-full bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

  // ===== Render
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 p-6">
      <h1 className="text-4xl font-bold text-center text-blue-700 mb-8">
        Your Jobs
      </h1>

      {/* Tabs */}
      <div className="flex justify-center gap-3 md:gap-4 mb-8">
        {["inprogress", "completed", "cancelled"].map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className={`px-5 py-2 rounded-full font-semibold transition-all ${
              selectedTab === tab
                ? "bg-blue-700 text-white shadow-md"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            }`}
          >
            {tab === "inprogress"
              ? "In Progress"
              : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-lg text-gray-600">Loading...</p>
      ) : (
        <div className="max-w-3xl md:max-w-4xl mx-auto space-y-8 pb-28">
          {selectedTab === "inprogress" &&
            (inProgressJobs.length ? (
              inProgressJobs.map((job) => (
                <JobCard
                  key={job._id}
                  job={job}
                  statusColor="text-green-600"
                />
              ))
            ) : (
              <p className="text-gray-600 text-center">No in-progress jobs.</p>
            ))}

          {selectedTab === "completed" &&
            (completedJobs.length ? (
              completedJobs.map((job) => (
                <JobCard key={job._id} job={job} statusColor="text-blue-600" />
              ))
            ) : (
              <p className="text-gray-600 text-center">No completed jobs.</p>
            ))}

          {selectedTab === "cancelled" &&
            (cancelledJobs.length ? (
              cancelledJobs.map((job) => (
                <JobCard
                  key={job._id}
                  job={job}
                  statusColor="text-red-600"
                  isCancelled
                />
              ))
            ) : (
              <p className="text-gray-600 text-center">No cancelled jobs.</p>
            ))}
        </div>
      )}

      <ModalUI
  modal={modal}
  setModal={setModal}
  handleConfirmJob={handleConfirmJob}
  handleDisputeJob={handleDisputeJob}
  fetchJobs={fetchJobs}
/>

    </div>
  );
};

export default YourJobs;
